// src/config/axios.js - UPDATED with JWT Token Refresh
// ✅ Authentication logic from laptop
// ✅ Base URL for server (without /api)

import axios from 'axios';
import toast from 'react-hot-toast';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// ============= TOKEN REFRESH LOGIC =============
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// ============= REQUEST INTERCEPTOR =============
api.interceptors.request.use(
  config => {
    // ✅ Check both sessionStorage and localStorage for token
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Dev logging
    if (process.env.NODE_ENV === 'development') {
      console.log('📤 API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        params: config.params,
        data: config.data
      });
    }
    
    return config;
  },
  error => {
    console.error('❌ Request Error:', error);
    toast.error('Failed to send request');
    return Promise.reject(error);
  }
);

// ============= RESPONSE INTERCEPTOR =============
api.interceptors.response.use(
  response => {
    // Dev logging
    if (process.env.NODE_ENV === 'development') {
      console.log('📥 API Response:', {
        url: response.config.url,
        status: response.status,
        data: response.data
      });
    }
    return response;
  },
  async error => {
    const originalRequest = error.config;

    // ✅ Handle 401 with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = sessionStorage.getItem('refreshToken') || localStorage.getItem('refreshToken');

      // No refresh token, logout
      if (!refreshToken) {
        toast.error('Session expired. Please login again.', {
          icon: '🔒',
          duration: 3000,
        });
        
        sessionStorage.clear();
        localStorage.clear();
        
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
        
        return Promise.reject(error);
      }

      try {
        // ✅ Call refresh endpoint
        const response = await axios.post(
          `${api.defaults.baseURL}/auth/refresh`,
          { refreshToken }
        );

        const { accessToken, refreshToken: newRefreshToken } = response.data;

        // Store new tokens
        const storage = sessionStorage.getItem('rememberMe') === 'true' ? localStorage : sessionStorage;
        storage.setItem('token', accessToken);
        if (newRefreshToken) {
          storage.setItem('refreshToken', newRefreshToken);
        }

        // Update default headers
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        // Process queued requests
        processQueue(null, accessToken);

        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout
        processQueue(refreshError, null);
        
        toast.error('Session expired. Please login again.', {
          icon: '🔒',
          duration: 3000,
        });
        
        sessionStorage.clear();
        localStorage.clear();
        
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // ✅ Handle other errors (existing logic preserved)
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          if (data.errors) {
            const errorMessages = Object.entries(data.errors)
              .map(([field, message]) => `${field}: ${message}`)
              .join('\n');
            toast.error(errorMessages, { duration: 5000 });
            throw new Error(errorMessages);
          }
          toast.error(data.message || 'Invalid request data');
          throw new Error(data.message || 'Bad Request');
          
        case 403:
          toast.error('You do not have permission to access this resource', {
            icon: '⛔',
            duration: 4000,
          });
          throw new Error('Forbidden');
          
        case 404:
          toast.error(data.message || 'Resource not found', { icon: '🔍' });
          throw new Error(data.message || 'Not Found');
          
        case 409:
          toast.error(data.message || 'Resource already exists', {
            icon: '⚠️',
            duration: 4000,
          });
          throw new Error(data.message || 'Conflict');
          
        case 500:
          toast.error('Server error. Please try again later.', {
            icon: '💥',
            duration: 5000,
          });
          throw new Error('Internal Server Error');
          
        default:
          toast.error(data.message || `Request failed with status ${status}`);
          throw new Error(data.message || 'Request Failed');
      }
    } else if (error.request) {
      console.error('🌐 Network Error:', error.request);
      toast.error('Network error. Please check your internet connection.', {
        icon: '📡',
        duration: 5000,
      });
      throw new Error('Network Error');
    } else {
      console.error('❌ Error:', error.message);
      toast.error(error.message || 'An unexpected error occurred');
      throw new Error(error.message || 'Unknown Error');
    }
  }
);

export default api;